// SNIGJ - Document Templates Configuration Page
import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { 
  FileText, Settings, Image, Upload, Save, Eye, Trash2, Plus,
  Building2, Scale, Printer, QrCode, AlertCircle, Check
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

const DocumentTemplatesPage = () => {
  const { user, language } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [jurisdictions, setJurisdictions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPreview, setShowPreview] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [editMode, setEditMode] = useState(false);
  
  // Form state
  const [form, setForm] = useState({
    template_type: '',
    jurisdiction_id: '',
    ministry_logo_url: '/logo-justice.png',
    jurisdiction_logo_url: '',
    header_text: 'RÉPUBLIQUE DÉMOCRATIQUE DU CONGO',
    subheader_text: 'MINISTÈRE DE LA JUSTICE ET GARDE DES SCEAUX',
    footer_text: 'SNIGJ - Système National Intégré de Gestion de la Justice',
    show_qr_code: true,
    show_signature_zone: true,
    primary_color: '#1e3a5f',
    secondary_color: '#d4af37'
  });

  const templateTypes = [
    { value: 'PERCEPTION_NOTE', label: language === 'fr' ? 'Note de Perception' : 'Fee Note' },
    { value: 'SUMMONS', label: language === 'fr' ? 'Convocation' : 'Summons' },
    { value: 'INVITATION', label: language === 'fr' ? 'Invitation' : 'Invitation' },
    { value: 'WARRANT', label: language === 'fr' ? 'Mandat' : 'Warrant' },
    { value: 'JUDGMENT', label: language === 'fr' ? 'Jugement' : 'Judgment' },
    { value: 'RELEASE_ORDER', label: language === 'fr' ? 'Ordre de libération' : 'Release Order' },
    { value: 'ARREST_WARRANT', label: language === 'fr' ? 'Mandat d\'arrêt' : 'Arrest Warrant' },
    { value: 'RECEIPT', label: language === 'fr' ? 'Reçu de paiement' : 'Payment Receipt' }
  ];

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [templatesRes, jurisdictionsRes] = await Promise.all([
        api.get('/admin/document-templates'),
        api.get('/system/jurisdictions')
      ]);
      setTemplates(templatesRes.data.templates || []);
      setJurisdictions(jurisdictionsRes.data.jurisdictions || []);
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveTemplate = async () => {
    try {
      if (editMode && selectedTemplate) {
        await api.put(`/admin/document-templates/${selectedTemplate.template_id}`, form);
        toast.success(language === 'fr' ? 'Modèle mis à jour' : 'Template updated');
      } else {
        await api.post('/admin/document-templates', form);
        toast.success(language === 'fr' ? 'Modèle créé' : 'Template created');
      }
      loadData();
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error');
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    if (!confirm(language === 'fr' ? 'Supprimer ce modèle ?' : 'Delete this template?')) return;
    try {
      await api.delete(`/admin/document-templates/${templateId}`);
      toast.success(language === 'fr' ? 'Modèle supprimé' : 'Template deleted');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error');
    }
  };

  const handleEditTemplate = (template) => {
    setSelectedTemplate(template);
    setForm({
      template_type: template.template_type,
      jurisdiction_id: template.jurisdiction_id,
      ministry_logo_url: template.ministry_logo_url,
      jurisdiction_logo_url: template.jurisdiction_logo_url,
      header_text: template.header_text,
      subheader_text: template.subheader_text,
      footer_text: template.footer_text,
      show_qr_code: template.show_qr_code,
      show_signature_zone: template.show_signature_zone,
      primary_color: template.primary_color,
      secondary_color: template.secondary_color
    });
    setEditMode(true);
  };

  const resetForm = () => {
    setForm({
      template_type: '',
      jurisdiction_id: '',
      ministry_logo_url: '/logo-justice.png',
      jurisdiction_logo_url: '',
      header_text: 'RÉPUBLIQUE DÉMOCRATIQUE DU CONGO',
      subheader_text: 'MINISTÈRE DE LA JUSTICE ET GARDE DES SCEAUX',
      footer_text: 'SNIGJ - Système National Intégré de Gestion de la Justice',
      show_qr_code: true,
      show_signature_zone: true,
      primary_color: '#1e3a5f',
      secondary_color: '#d4af37'
    });
    setSelectedTemplate(null);
    setEditMode(false);
  };

  const handleLogoUpload = async (e, type) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // For now, use URL.createObjectURL for preview
    // In production, this would upload to a storage service
    const url = URL.createObjectURL(file);
    if (type === 'ministry') {
      setForm({...form, ministry_logo_url: url});
    } else {
      setForm({...form, jurisdiction_logo_url: url});
    }
    toast.info(language === 'fr' ? 'Logo chargé (prévisualisation locale)' : 'Logo loaded (local preview)');
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="document-templates-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-heading text-3xl font-bold tracking-tight">
              {language === 'fr' ? 'Modèles de Documents' : 'Document Templates'}
            </h1>
            <p className="text-muted-foreground mt-1">
              {language === 'fr' 
                ? 'Configurez les en-têtes, logos et formats des documents officiels' 
                : 'Configure headers, logos and formats of official documents'}
            </p>
          </div>
        </div>

        <Tabs defaultValue="templates" className="space-y-4">
          <TabsList>
            <TabsTrigger value="templates" className="gap-2">
              <FileText className="w-4 h-4" />
              {language === 'fr' ? 'Modèles existants' : 'Existing Templates'}
            </TabsTrigger>
            <TabsTrigger value="create" className="gap-2">
              <Plus className="w-4 h-4" />
              {editMode 
                ? (language === 'fr' ? 'Modifier modèle' : 'Edit Template')
                : (language === 'fr' ? 'Nouveau modèle' : 'New Template')}
            </TabsTrigger>
          </TabsList>

          {/* Existing Templates */}
          <TabsContent value="templates">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {templates.length === 0 ? (
                <Card className="col-span-full">
                  <CardContent className="p-8 text-center">
                    <FileText className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                    <p className="text-muted-foreground">
                      {language === 'fr' ? 'Aucun modèle configuré' : 'No templates configured'}
                    </p>
                  </CardContent>
                </Card>
              ) : (
                templates.map((template) => (
                  <Card key={template.template_id} className="hover:shadow-lg transition-shadow">
                    <CardHeader className="pb-2">
                      <div className="flex items-start justify-between">
                        <div>
                          <CardTitle className="text-base">
                            {templateTypes.find(t => t.value === template.template_type)?.label || template.template_type}
                          </CardTitle>
                          <CardDescription>
                            {template.jurisdiction_name || (language === 'fr' ? 'Toutes juridictions' : 'All jurisdictions')}
                          </CardDescription>
                        </div>
                        <Badge variant={template.is_active ? 'default' : 'secondary'}>
                          {template.is_active ? (language === 'fr' ? 'Actif' : 'Active') : (language === 'fr' ? 'Inactif' : 'Inactive')}
                        </Badge>
                      </div>
                    </CardHeader>
                    <CardContent>
                      {/* Mini Preview */}
                      <div className="border rounded p-3 mb-4 bg-white dark:bg-slate-900">
                        <div className="flex items-center justify-center gap-2 text-center">
                          {template.ministry_logo_url && (
                            <img src={template.ministry_logo_url} alt="" className="w-8 h-8 object-contain" />
                          )}
                          <div className="text-[8px]">
                            <p className="font-bold">{template.header_text}</p>
                            <p>{template.subheader_text}</p>
                          </div>
                          {template.jurisdiction_logo_url && (
                            <img src={template.jurisdiction_logo_url} alt="" className="w-8 h-8 object-contain" />
                          )}
                        </div>
                        <div className="flex justify-between mt-2 text-[6px] text-muted-foreground">
                          {template.show_qr_code && <span>📱 QR</span>}
                          {template.show_signature_zone && <span>✍️ Signature</span>}
                        </div>
                      </div>
                      
                      <div className="flex gap-2">
                        <Button variant="outline" size="sm" className="flex-1" onClick={() => { setSelectedTemplate(template); setShowPreview(true); }}>
                          <Eye className="w-3 h-3 mr-1" />
                          {language === 'fr' ? 'Aperçu' : 'Preview'}
                        </Button>
                        <Button variant="outline" size="sm" className="flex-1" onClick={() => handleEditTemplate(template)}>
                          <Settings className="w-3 h-3 mr-1" />
                          {language === 'fr' ? 'Modifier' : 'Edit'}
                        </Button>
                        <Button variant="ghost" size="icon" className="text-red-500" onClick={() => handleDeleteTemplate(template.template_id)}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </TabsContent>

          {/* Create/Edit Template */}
          <TabsContent value="create">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Form */}
              <Card>
                <CardHeader>
                  <CardTitle>
                    {editMode 
                      ? (language === 'fr' ? 'Modifier le modèle' : 'Edit Template')
                      : (language === 'fr' ? 'Créer un modèle' : 'Create Template')}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Template Type */}
                  <div className="space-y-2">
                    <Label>{language === 'fr' ? 'Type de document' : 'Document Type'}</Label>
                    <Select value={form.template_type} onValueChange={(v) => setForm({...form, template_type: v})}>
                      <SelectTrigger>
                        <SelectValue placeholder={language === 'fr' ? 'Sélectionner...' : 'Select...'} />
                      </SelectTrigger>
                      <SelectContent>
                        {templateTypes.map((type) => (
                          <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Jurisdiction */}
                  <div className="space-y-2">
                    <Label>{language === 'fr' ? 'Juridiction' : 'Jurisdiction'}</Label>
                    <Select value={form.jurisdiction_id || "all"} onValueChange={(v) => setForm({...form, jurisdiction_id: v === "all" ? "" : v})}>
                      <SelectTrigger>
                        <SelectValue placeholder={language === 'fr' ? 'Toutes juridictions' : 'All jurisdictions'} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">{language === 'fr' ? 'Toutes juridictions' : 'All jurisdictions'}</SelectItem>
                        {jurisdictions.map((j) => (
                          <SelectItem key={j.jurisdiction_id} value={j.jurisdiction_id}>{j.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Logos */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>{language === 'fr' ? 'Logo Ministère' : 'Ministry Logo'}</Label>
                      <div className="border rounded p-3 text-center">
                        {form.ministry_logo_url && (
                          <img src={form.ministry_logo_url} alt="" className="w-16 h-16 mx-auto object-contain mb-2" />
                        )}
                        <label className="cursor-pointer">
                          <input type="file" accept="image/*" className="hidden" onChange={(e) => handleLogoUpload(e, 'ministry')} />
                          <Button type="button" variant="outline" size="sm" className="gap-2">
                            <Upload className="w-3 h-3" />
                            {language === 'fr' ? 'Changer' : 'Change'}
                          </Button>
                        </label>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>{language === 'fr' ? 'Logo Juridiction' : 'Jurisdiction Logo'}</Label>
                      <div className="border rounded p-3 text-center">
                        {form.jurisdiction_logo_url ? (
                          <img src={form.jurisdiction_logo_url} alt="" className="w-16 h-16 mx-auto object-contain mb-2" />
                        ) : (
                          <Building2 className="w-16 h-16 mx-auto text-muted-foreground mb-2" />
                        )}
                        <label className="cursor-pointer">
                          <input type="file" accept="image/*" className="hidden" onChange={(e) => handleLogoUpload(e, 'jurisdiction')} />
                          <Button type="button" variant="outline" size="sm" className="gap-2">
                            <Upload className="w-3 h-3" />
                            {language === 'fr' ? 'Ajouter' : 'Add'}
                          </Button>
                        </label>
                      </div>
                    </div>
                  </div>

                  {/* Header Text */}
                  <div className="space-y-2">
                    <Label>{language === 'fr' ? 'Texte en-tête principal' : 'Main Header Text'}</Label>
                    <Input value={form.header_text} onChange={(e) => setForm({...form, header_text: e.target.value})} />
                  </div>

                  {/* Subheader Text */}
                  <div className="space-y-2">
                    <Label>{language === 'fr' ? 'Sous-titre' : 'Subheader'}</Label>
                    <Input value={form.subheader_text} onChange={(e) => setForm({...form, subheader_text: e.target.value})} />
                  </div>

                  {/* Footer Text */}
                  <div className="space-y-2">
                    <Label>{language === 'fr' ? 'Texte pied de page' : 'Footer Text'}</Label>
                    <Input value={form.footer_text} onChange={(e) => setForm({...form, footer_text: e.target.value})} />
                  </div>

                  {/* Options */}
                  <div className="flex items-center gap-6">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input 
                        type="checkbox" 
                        checked={form.show_qr_code} 
                        onChange={(e) => setForm({...form, show_qr_code: e.target.checked})}
                        className="rounded"
                      />
                      <QrCode className="w-4 h-4" />
                      <span className="text-sm">{language === 'fr' ? 'QR Code' : 'QR Code'}</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input 
                        type="checkbox" 
                        checked={form.show_signature_zone} 
                        onChange={(e) => setForm({...form, show_signature_zone: e.target.checked})}
                        className="rounded"
                      />
                      <span className="text-sm">{language === 'fr' ? 'Zone signature' : 'Signature Zone'}</span>
                    </label>
                  </div>

                  {/* Colors */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>{language === 'fr' ? 'Couleur principale' : 'Primary Color'}</Label>
                      <div className="flex gap-2">
                        <input 
                          type="color" 
                          value={form.primary_color} 
                          onChange={(e) => setForm({...form, primary_color: e.target.value})}
                          className="w-12 h-10 rounded cursor-pointer"
                        />
                        <Input value={form.primary_color} onChange={(e) => setForm({...form, primary_color: e.target.value})} className="flex-1" />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>{language === 'fr' ? 'Couleur secondaire' : 'Secondary Color'}</Label>
                      <div className="flex gap-2">
                        <input 
                          type="color" 
                          value={form.secondary_color} 
                          onChange={(e) => setForm({...form, secondary_color: e.target.value})}
                          className="w-12 h-10 rounded cursor-pointer"
                        />
                        <Input value={form.secondary_color} onChange={(e) => setForm({...form, secondary_color: e.target.value})} className="flex-1" />
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex gap-2 pt-4">
                    <Button onClick={handleSaveTemplate} className="flex-1 gap-2" disabled={!form.template_type}>
                      <Save className="w-4 h-4" />
                      {editMode 
                        ? (language === 'fr' ? 'Mettre à jour' : 'Update')
                        : (language === 'fr' ? 'Créer' : 'Create')}
                    </Button>
                    {editMode && (
                      <Button variant="outline" onClick={resetForm}>
                        {language === 'fr' ? 'Annuler' : 'Cancel'}
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Live Preview */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Eye className="w-5 h-5" />
                    {language === 'fr' ? 'Aperçu en direct' : 'Live Preview'}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="border rounded p-6 bg-white dark:bg-slate-900">
                    {/* Header with logos */}
                    <div className="text-center border-b-2 pb-4 mb-4" style={{ borderColor: form.primary_color }}>
                      <div className="flex items-center justify-center gap-4 mb-3">
                        {form.ministry_logo_url && (
                          <img src={form.ministry_logo_url} alt="Ministry" className="w-16 h-16 object-contain" />
                        )}
                        <div>
                          <h1 className="text-sm font-bold" style={{ color: form.primary_color }}>{form.header_text}</h1>
                          <h2 className="text-xs">{form.subheader_text}</h2>
                        </div>
                        {form.jurisdiction_logo_url ? (
                          <img src={form.jurisdiction_logo_url} alt="Jurisdiction" className="w-16 h-16 object-contain" />
                        ) : (
                          <div className="w-16 h-16 border-2 border-dashed rounded flex items-center justify-center">
                            <Building2 className="w-8 h-8 text-muted-foreground" />
                          </div>
                        )}
                      </div>
                      <h3 className="text-base font-bold border inline-block px-4 py-2" style={{ borderColor: form.primary_color }}>
                        {templateTypes.find(t => t.value === form.template_type)?.label || (language === 'fr' ? 'TYPE DE DOCUMENT' : 'DOCUMENT TYPE')}
                      </h3>
                    </div>

                    {/* Sample Content */}
                    <div className="space-y-3 mb-6 text-sm">
                      <div className="flex justify-between py-2 border-b">
                        <span className="text-muted-foreground">{language === 'fr' ? 'Numéro de référence' : 'Reference Number'}</span>
                        <span className="font-mono">DOC-2026-001234</span>
                      </div>
                      <div className="flex justify-between py-2 border-b">
                        <span className="text-muted-foreground">{language === 'fr' ? 'Date d\'émission' : 'Issue Date'}</span>
                        <span>{new Date().toLocaleDateString()}</span>
                      </div>
                      <div className="flex justify-between py-2 border-b">
                        <span className="text-muted-foreground">{language === 'fr' ? 'Bénéficiaire' : 'Beneficiary'}</span>
                        <span>Jean Exemple</span>
                      </div>
                    </div>

                    {/* Amount Box */}
                    <div className="text-center p-4 border-2 mb-6" style={{ borderColor: form.secondary_color, backgroundColor: `${form.secondary_color}10` }}>
                      <p className="text-xs uppercase">{language === 'fr' ? 'Montant' : 'Amount'}</p>
                      <p className="text-2xl font-bold" style={{ color: form.primary_color }}>50.00 USD</p>
                    </div>

                    {/* Footer Section */}
                    <div className="flex justify-between items-end pt-4 border-t">
                      {form.show_qr_code && (
                        <div className="text-center">
                          <div className="w-16 h-16 border rounded flex items-center justify-center bg-muted">
                            <QrCode className="w-10 h-10" />
                          </div>
                          <p className="text-[8px] mt-1 text-muted-foreground">{language === 'fr' ? 'Scannez pour vérifier' : 'Scan to verify'}</p>
                        </div>
                      )}
                      {form.show_signature_zone && (
                        <div className="text-right">
                          <div className="w-32 border-b border-black mb-1"></div>
                          <p className="text-[8px] text-muted-foreground">{language === 'fr' ? 'Signature et cachet' : 'Signature and stamp'}</p>
                        </div>
                      )}
                    </div>

                    {/* Footer Text */}
                    <div className="text-center text-[8px] text-muted-foreground mt-4 pt-4 border-t">
                      <p>{form.footer_text}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Preview Modal */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{language === 'fr' ? 'Aperçu du modèle' : 'Template Preview'}</DialogTitle>
          </DialogHeader>
          {selectedTemplate && (
            <div className="border rounded p-6 bg-white dark:bg-slate-900">
              <div className="text-center border-b-2 pb-4 mb-4">
                <div className="flex items-center justify-center gap-4 mb-3">
                  {selectedTemplate.ministry_logo_url && (
                    <img src={selectedTemplate.ministry_logo_url} alt="" className="w-20 h-20 object-contain" />
                  )}
                  <div>
                    <h1 className="text-lg font-bold">{selectedTemplate.header_text}</h1>
                    <h2 className="text-sm">{selectedTemplate.subheader_text}</h2>
                  </div>
                  {selectedTemplate.jurisdiction_logo_url && (
                    <img src={selectedTemplate.jurisdiction_logo_url} alt="" className="w-20 h-20 object-contain" />
                  )}
                </div>
                <h3 className="text-base font-bold border inline-block px-6 py-2">
                  {templateTypes.find(t => t.value === selectedTemplate.template_type)?.label}
                </h3>
              </div>
              <p className="text-center text-muted-foreground py-8">
                [{language === 'fr' ? 'Contenu du document ici' : 'Document content here'}]
              </p>
              <div className="text-center text-xs text-muted-foreground pt-4 border-t">
                <p>{selectedTemplate.footer_text}</p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};

export default DocumentTemplatesPage;
